/**
 * Claude Code for Kids — SEO meta + FAQ copy shared by client and server.
 *
 * ONE SOURCE OF TRUTH: the page (client/src/pages/kids/kids-content.ts
 * re-exports these), the SSR head (server/page-meta.ts emits KIDS_SEO.title
 * so the served title matches the hydrated one), and the FAQPage JSON-LD
 * (shared/workshop-content.ts maps KIDS_FAQS into faqItems) all read from
 * here — the previous 4-item copy in workshop-content had already forked
 * from the page's 21 questions.
 */

export const KIDS_SEO = {
  title: "Claude Code Camp for Teens, Students, and Kids | AdVenture Media",
  description:
    "Claude Code training workshops for kids, teens, and students of all ages. Taught by Isaac Rudansky and AdVenture Media, Claude Code for kids is a safety-first approach to AI coding for kids where every student ships a real app. Trusted by over 300,000 students globally.",
} as const;

/* ── Course JSON-LD pieces (Google Course rich-result eligibility) ──
   Shared by BOTH Course emitters (server/page-meta.ts for SSR + crawlers,
   client/src/pages/event-detail.tsx dev mirror) so the two nodes can never
   disagree. Dates/times mirror sessions 1–4 in KIDS_SESSIONS
   (shared/kids-camp.ts) — Orientation and Demo Night are extra touchpoints,
   not coursework. If the cohort moves, update this alongside BOTH session
   registries. */

/** Total instruction time: 4 live build sessions × 90 minutes. */
export const KIDS_COURSE_WORKLOAD = "PT6H";

/** The four build sessions: Tue–Thu Aug 25–27 + Mon Aug 31, 6:30–8:00 PM ET.
 *  One Tue/Wed/Thu/Mon each falls inside the one-week window, so byDay +
 *  weekly frequency enumerate exactly the four real session dates. */
export const KIDS_COURSE_SCHEDULE = {
  "@type": "Schedule",
  startDate: "2026-08-25",
  endDate: "2026-08-31",
  repeatCount: 4,
  repeatFrequency: "Weekly",
  byDay: [
    "https://schema.org/Tuesday",
    "https://schema.org/Wednesday",
    "https://schema.org/Thursday",
    "https://schema.org/Monday",
  ],
  startTime: "18:30",
  endTime: "20:00",
  scheduleTimezone: "America/New_York",
  duration: "PT1H30M",
} as const;

export type KidsFaqCategory = "technology" | "requirements" | "enrollment" | "general";
export interface KidsFaq {
  q: string;
  a: string;
  category: KidsFaqCategory;
}

export const KIDS_FAQS: KidsFaq[] = [
  // ── Technology & safety ──
  {
    q: "Does my kid need coding experience?",
    category: "technology",
    a: "None. Campers build by directing Claude in plain English, which is exactly the skill we teach. Kids who already tinker won't be bored either: the ceiling on their own project is as high as they want to push it.",
  },
  {
    q: "Is the AI chat safe? Is it monitored?",
    category: "technology",
    a: "Sessions are live and instructor-led, a parent is at the elbow, and campers work on the parent's account, so you can see everything, always. Claude itself is built by Anthropic, the AI lab best known for its safety obsession, and Session 4 teaches your kid the judgment no tool can provide.",
  },
  {
    q: "Will my child be exposed to inappropriate content?",
    category: "technology",
    a: "It's the first thing we design against. Before camp, our team gives each family a customized CLAUDE.md file: a plain-text set of instructions that tells Claude, up front, that it's working with a minor, so it teaches, talks, and responds in an age-appropriate way. No single safeguard is perfect, and we won't pretend it covers every possible edge case, but Claude is built by Anthropic, the lab best known for making the safest, most harmless AI, and campers always work on your account with a parent nearby and a live instructor on screen.",
  },
  {
    q: "Isn't this just more screen time?",
    category: "technology",
    a: "It's the other kind. There's screen time where the feed happens to your kid, and screen time where your kid makes the thing. Ninety structured minutes of building, presenting, and debating is the second kind, and it tends to leak into the week as making, not scrolling.",
  },

  // ── What you'll need ──
  {
    q: "What does my child need to take part?",
    category: "requirements",
    a: "A computer or an iPad with a keyboard, plus a steady internet connection. A phone or a tablet on its own is not enough for building.\n\nEnough comfort at a keyboard to hunt and peck. If typing is a real barrier for your child, tell us and we will set up voice dictation together.\n\nA parent's Claude account, which we set up with you at Parent Orientation. More on the account in the next two questions.",
  },
  {
    q: "What if I don't have a Claude account?",
    category: "requirements",
    a: "Most families don't yet, and that is completely fine. Creating one is free, and we set it up with you step by step at Parent Orientation, before your child ever logs on. For the month of camp you will want the paid plan, Claude Pro, since that is what unlocks Claude Code, the tool campers build with. It is about $20 a month and you can cancel anytime.",
  },
  {
    q: "How do I sign up for Claude?",
    category: "requirements",
    a: "Go to claude.ai, create a free account with your email, and then upgrade to Claude Pro so Claude Code is unlocked. If you would rather not sort it out alone, that is exactly what Parent Orientation is for: we walk through the whole setup together, live, so nothing is a surprise on Build Night.",
  },
  {
    q: "Does my kid need an email address or their own AI account?",
    category: "requirements",
    a: "No, and under Anthropic's terms they can't have one. Anthropic requires account holders to be 18 or older, so campers build on YOUR Claude account with you nearby. Every camp email goes to your inbox, never your kid's, and we set the whole thing up together at Parent Orientation on Aug 24.",
  },
  {
    q: "What exactly do I need to buy from Anthropic, and what does it cost?",
    category: "requirements",
    a: "One paid Claude plan on your account for the month of camp. A Claude Pro subscription (about $20/month, cancel anytime) is the minimum that unlocks Claude Code, the tool campers build with. That is a separate charge from Anthropic, not part of your camp tuition, and it is the only outside cost. We walk you through signing up, step by step, at Parent Orientation so nothing is a surprise on Build Night.",
  },

  // ── Enrollment & scheduling ──
  {
    q: "Is my kid the right age?",
    category: "enrollment",
    a: "Camp is built for ages 10 to 16, and it is designed for 16 and under. Below 10, most kids will find it hard to keep up on their own. That said, we warmly encourage enrolling an 8 or 9 year old alongside an older sibling, to watch, help, and take part at their own pace. If you are on the fence, email us and we will talk it through honestly.",
  },
  {
    q: "How big is the cohort?",
    category: "enrollment",
    a: "We don't have a specific limit. We aim to keep each cohort small enough, and to bring enough instructors, that every camper gets individual attention and real Q&A time. Three instructors are on the roster, with at least two adults from our team in every session.",
  },
  {
    q: "What if we miss a session?",
    category: "enrollment",
    a: "Every session is recorded and the recordings are yours to keep. Cameras are optional for campers, so a camper who keeps theirs on may appear in that session's recording. Miss a night, watch it that evening, and the homework review keeps your kid on pace for the next one.",
  },
  {
    q: "Will recordings be available?",
    category: "enrollment",
    a: "Yes. Every session is recorded and yours to keep, forever. Cameras are optional for campers, so a camper who keeps theirs on may appear in the recording. Families keep their recordings and we never use them in marketing. Catch up on a missed night or let your kid rewatch a step while they build.",
  },
  {
    q: "What's the refund policy?",
    category: "enrollment",
    a: "The Money-Back Satisfaction Guarantee: you can claim a full, 100% refund any time from purchase until one hour after the first session ends. Join Session 1, and if camp isn't the right fit for your child, just tell us. After that window closes, tuition is final. If we ever cancel the program outright, you get a full refund.",
  },

  // ── The camp itself ──
  {
    q: "Do I need to sit with my kid the whole time?",
    category: "general",
    a: "You need to be around, and the account is yours, so plan to be within earshot with occasional cameos. The last 15 minutes of every session is a parent debrief built for you. Many parents end up pulling a chair over anyway, because it's genuinely fun.",
  },
  {
    q: "Can parents watch and attend with their kids?",
    category: "general",
    a: "Yes, and we love when you do. The account is yours and a parent needs to be nearby anyway, so you are welcome to pull up a chair and build right alongside your kid. Plenty of parents tell us it is the most fun they have had learning something new in years.",
  },
  {
    q: "What will my child actually build?",
    category: "general",
    a: "Their own real app or game, chosen by them and published live at its own shareable link. A workout tracker, a vocabulary game, an outfit picker, whatever they are genuinely excited about. They bring the idea, and the instructors make sure it actually ships. By Demo Night, every camper has something that runs and that they can show family and friends.",
  },
  {
    q: "Will there be homework?",
    category: "general",
    a: "Yes, but the good kind. Small missions between sessions, reviewed by instructors, so momentum carries across the days off. Most of it amounts to using the thing you built and bringing back one idea to make it better, which tends to feel more like play than homework.",
  },
  {
    q: "Can they make AI art?",
    category: "general",
    a: "If your child wants to, yes. Alongside building their app, campers can explore making images and art with AI, and some fold it right into their project. It is completely optional and driven by whatever the kid is excited to create.",
  },
  {
    q: "What does my kid actually keep?",
    category: "general",
    a: "Their app, live at a shareable link. The recordings of all four sessions. The Camp Field Guide (our prompt playbook, kid edition). A mailed, framable diploma. And two weeks of build support after camp ends.",
  },
  {
    q: "Will this help with school?",
    category: "general",
    a: "It isn't tutoring, and we won't pretend otherwise. What it builds: precise thinking, clear writing (prompts are writing), the iterate-until-good habit, and the judgment to use AI for schoolwork honestly. Teachers notice those.",
  },
  {
    q: "What happens after camp?",
    category: "general",
    a: "Two weeks of build support through Sep 14 while the habit sets, the project gallery, and first priority on future cohorts. Fair warning: most kids don't stop at one app.",
  },
];
